import React, { useEffect, useState, useMemo, useCallback } from "react";
import { api, downloadAttachment, signOut } from "./supabaseApi";
import "./App.css";
import Reports from "./Reports";
import TaskComments from "./TaskComments";

const errMsg = (e) => {
  const d = e?.response?.data?.detail || e?.error;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x.msg || JSON.stringify(x)).join(", ");
  return e?.message || "Something went wrong";
};

// ---------- Toasts ----------
let toastId = 0;
const ToastCtx = React.createContext({ push: () => {} });
const useToast = () => React.useContext(ToastCtx);
function ToastProvider({ children }) {
  const [items, setItems] = useState([]);
  const push = useCallback((msg, kind = "ok", title = "") => {
    const id = ++toastId;
    setItems(x => [...x, { id, msg, kind, title }]);
    setTimeout(() => setItems(x => x.filter(t => t.id !== id)), 3800);
  }, []);
  return (
    <ToastCtx.Provider value={{ push }}>
      {children}
      <div className="tc2" data-testid="toast-container">
        {items.map(t => (
          <div key={t.id} className={`toast ${t.kind}`} data-testid={`toast-${t.kind}`}>
            <div className="tbdy">
              {t.title && <div className="ttl">{t.title}</div>}
              <div className="tmsg">{t.msg}</div>
            </div>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

// ---------- Login ----------
function Login({ onLogin }) {
  const [tab, setTab] = useState("staff");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { push } = useToast();

  useEffect(() => {
    setEmail(""); setPw("");
    setErr("");
  }, [tab]);

  const submit = async () => {
    setErr(""); setLoading(true);
    try {
      const data = await api.post("/auth/login", { email, password: pw });
      if (tab === "admin" && data.user.role !== "owner") {
        await signOut();
        throw new Error("This is a staff account. Please use Staff Login.");
      }
      if (tab === "staff" && data.user.role === "owner") {
        await signOut();
        throw new Error("This is an Owner account. Please use Admin / Owner login.");
      }
      localStorage.setItem("wam_token", data.token);
      localStorage.setItem("wam_user", JSON.stringify(data.user));
      push(`Welcome, ${data.user.name || "user"}`, "ok", "Signed in");
      onLogin(data.user);
    } catch (e) { setErr(errMsg(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="auth-wrap">
      <div className="card">
        <div className="logo">
          <div className="lg-icon">EP</div>
          <h1>Erawat Pharma Limited</h1>
          <p>Work Assignment &amp; Monitoring System</p>
        </div>
        <div className="tabs">
          <button data-testid="tab-staff" className={`tab ${tab === "staff" ? "on" : ""}`} onClick={() => setTab("staff")}>Staff Login</button>
          <button data-testid="tab-admin" className={`tab ${tab === "admin" ? "on" : ""}`} onClick={() => setTab("admin")}>Admin / Owner</button>
        </div>
        {err && <div className="aerr" data-testid="login-error">{err}</div>}
        <>
            <div className="fg"><label>Email</label>
              <input data-testid="login-email" className="fc" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" /></div>
            <div className="fg"><label>Password</label>
              <input data-testid="login-password" className="fc" type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="•••••••" /></div>
            <div className="ib">
              <b>{tab === "admin" ? "🔐 Owner Login" : "👥 Staff Login"}</b>
              Use the credentials provided by your company administrator.
            </div>
        </>
        <button data-testid="login-submit" className="btn bp bfull" disabled={loading} onClick={submit}>
          {loading ? "Signing in..." : `Sign In as ${tab === "admin" ? "Owner" : "Staff"}`}
        </button>
        <div className="footer-brand">Secure company workspace</div>
      </div>
    </div>
  );
}

// ---------- Modal ----------
function Modal({ title, onClose, children, footer, large }) {
  return (
    <div className="ov" onClick={onClose}>
      <div className={`md ${large ? "mdlg" : ""}`} onClick={e => e.stopPropagation()}>
        <div className="mh"><div className="mt2">{title}</div><button className="mx" onClick={onClose}>×</button></div>
        <div className="mb">{children}</div>
        {footer && <div className="mf">{footer}</div>}
      </div>
    </div>
  );
}

// ---------- Helpers ----------
const initials = (n) => (n || "?").split(" ").map(x => x[0]).slice(0, 2).join("").toUpperCase();
const statusBadge = (s) => s === "Completed" ? "bdon" : s === "In Process" ? "binp" : "bpnd";
const prioBadge = (p) => p === "High" ? "bphi" : p === "Low" ? "bplo" : "bpmed";
const isOverdue = (t) => t.due_date && t.status !== "Completed" && new Date(t.due_date) < new Date(new Date().toDateString());
const progress = (t) => {
  const acts = t.activities || [];
  if (!acts.length) return t.status === "Completed" ? 100 : t.status === "In Process" ? 50 : 0;
  const done = acts.filter(a => a.done).length;
  return Math.round((done / acts.length) * 100);
};

// ---------- Dashboard ----------
function Dashboard({ tasks, users }) {
  const total = tasks.length;
  const pending = tasks.filter(t => t.status === "Pending").length;
  const inp = tasks.filter(t => t.status === "In Process").length;
  const done = tasks.filter(t => t.status === "Completed").length;
  const overdue = tasks.filter(isOverdue);
  const staffName = (id) => users.find(u => u.id === id)?.name || "—";
  return (
    <div>
      <div className="ph"><div className="pt">Dashboard <small style={{ fontSize: 12, color: "var(--mt)", fontWeight: 500 }}>{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</small></div></div>
      <div className="stats">
        <div className="stat" data-testid="stat-total"><div className="stico" style={{ background: "#eff6ff" }}>📋</div><div className="statv">{total}</div><div className="statl">Total Tasks</div></div>
        <div className="stat" data-testid="stat-pending"><div className="stico" style={{ background: "var(--nb)" }}>⏳</div><div className="statv" style={{ color: "var(--nc)" }}>{pending}</div><div className="statl">Pending</div></div>
        <div className="stat" data-testid="stat-inprocess"><div className="stico" style={{ background: "var(--yb)" }}>⚡</div><div className="statv" style={{ color: "var(--w)" }}>{inp}</div><div className="statl">In Process</div></div>
        <div className="stat" data-testid="stat-completed"><div className="stico" style={{ background: "var(--gb)" }}>✔</div><div className="statv" style={{ color: "var(--g)" }}>{done}</div><div className="statl">Completed</div></div>
      </div>
      <div className="xc"><div className="xch"><div className="xct">Recent Tasks</div></div>
        <p className="table-scroll-hint">Swipe sideways to see all task details →</p>
        <div className="tw" tabIndex={0} role="region" aria-label="Recent tasks, scroll horizontally for all columns"><table><thead><tr><th>Task</th><th>Staff</th><th>Assigned By</th><th>Due</th><th>Status</th><th>Progress</th></tr></thead>
          <tbody>{tasks.slice(0, 8).map(t => (
            <tr key={t.id}><td>{t.name}</td><td>{staffName(t.assigned_to)}</td><td>{t.assigned_by_name || "Not recorded"}</td><td>{t.due_date || "—"}</td>
              <td><span className={`badge ${statusBadge(t.status)}`}>{t.status}</span></td>
              <td><span className="pbar"><span className="pfil" style={{ width: `${progress(t)}%` }} /></span>{progress(t)}%</td></tr>
          ))}
          {!tasks.length && <tr><td colSpan="6" className="empty">No tasks yet</td></tr>}</tbody></table></div>
      </div>
      {!!overdue.length && (
        <div className="xc"><div className="xch" style={{ background: "#fff5f5", borderRadius: "10px 10px 0 0" }}><div className="xct" style={{ color: "var(--r)" }}>⚠ Overdue ({overdue.length})</div></div>
          <p className="table-scroll-hint">Swipe sideways to see all task details →</p>
          <div className="tw" tabIndex={0} role="region" aria-label="Overdue tasks, scroll horizontally for all columns"><table><thead><tr><th>Task</th><th>Staff</th><th>Due</th><th>Status</th></tr></thead>
            <tbody>{overdue.map(t => (
              <tr key={t.id} className="rov"><td>{t.name}</td><td>{staffName(t.assigned_to)}</td><td>{t.due_date}</td>
                <td><span className={`badge ${statusBadge(t.status)}`}>{t.status}</span></td></tr>
            ))}</tbody></table></div></div>
      )}
    </div>
  );
}

// ---------- Task Modal ----------
export function TaskModal({ task, users, groups, currentUser, onSave, onClose }) {
  const [f, setF] = useState({
    name: task?.name || "", description: task?.description || "",
    priority: task?.priority || "Medium", status: task?.status || "Pending",
    assigned_to: task?.assigned_to || "", group_id: task?.group_id || "",
    assigned_date: task?.assigned_date || new Date().toISOString().slice(0, 10),
    due_date: task?.due_date || "",
    activities: task?.activities || [],
  });
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [attachments, setAttachments] = useState(task?.attachments || []);
  const savedTask = React.useRef(task || null);
  const saving = React.useRef(false);
  const { push } = useToast();
  const assignableUsers = users.filter(u => u.role === "staff" && !u.is_system_account);

  const save = async () => {
    if (saving.current) return;
    setErr("");
    if (!f.name.trim()) return setErr("Task name is required");
    if (!f.assigned_to && !f.group_id) return setErr("Assign to staff or group");
    if (!f.due_date) return setErr("Due date is required");
    saving.current = true;
    setUploading(true);
    try {
      const payload = { ...f, assigned_to: f.assigned_to || null, group_id: f.group_id || null };
      const saved = savedTask.current?.id ? await api.put(`/tasks/${savedTask.current.id}`, payload) : await api.post("/tasks", payload);
      savedTask.current = saved;
      const failed = [];
      for (const file of pendingFiles) {
        try {
          const fd = new FormData(); fd.append("file", file);
          const attachment = await api.upload(`/tasks/${saved.id}/attachments`, fd);
          setAttachments(previous => [...previous, attachment]);
        } catch (error) { failed.push({ file, message: errMsg(error) }); }
      }
      setPendingFiles(failed.map(item => item.file));
      if (failed.length) {
        setErr(`Task saved, but these files were not uploaded: ${failed.map(item => `${item.file.name}: ${item.message}`).join("; ")}. Click Save Task to retry, or remove the failed files. The task will not be created again.`);
        return;
      }
      push(task?.id ? "Task updated" : "Task created", "ok");
      onSave(saved);
    } catch (e) { setErr(errMsg(e)); }
    finally { saving.current = false; setUploading(false); }
  };

  const uploadFile = (e) => {
    const files = Array.from(e.target.files || []);
    const oversized = files.filter(file => file.size > 10 * 1024 * 1024);
    setErr(oversized.length ? `Each file must be 10 MB or smaller: ${oversized.map(file => file.name).join(", ")}` : "");
    setPendingFiles(previous => [...previous, ...files.filter(file => file.size <= 10 * 1024 * 1024)]);
    e.target.value = "";
  };
  const close = () => { if (!saving.current) { if (savedTask.current?.id && !task?.id) onSave(savedTask.current); else onClose(); } };

  return (
    <Modal title={task?.id ? "Edit Task" : "Add Task"} onClose={close} large
      footer={<><button className="btn bs" disabled={uploading} onClick={close}>{savedTask.current?.id && !task?.id ? "Close" : "Cancel"}</button><button data-testid="task-save" className="btn bp" disabled={uploading} onClick={save}>{uploading ? "Saving task and files…" : "Save Task"}</button></>}>
      {err && <div className="aerr">{err}</div>}
      <div className="fr">
        <div className="fg"><label>Task Name *</label><input data-testid="task-name" className="fc" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} /></div>
        <div className="fg"><label>Assign To Staff</label>
          <select data-testid="task-assignee" className="fc" value={f.assigned_to} onChange={e => setF({ ...f, assigned_to: e.target.value, group_id: "" })}>
            <option value="">— Select —</option>
            {assignableUsers.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
          </select></div>
      </div>
      <div className="fr">
        <div className="fg"><label>Or Assign to Group</label>
          <select className="fc" value={f.group_id} onChange={e => setF({ ...f, group_id: e.target.value, assigned_to: "" })}>
            <option value="">— None —</option>
            {groups.map(g => <option key={g.id} value={g.id}>{g.name} ({g.members.length} members)</option>)}
          </select></div>
        <div className="fg"><label>Priority</label>
          <select className="fc" value={f.priority} onChange={e => setF({ ...f, priority: e.target.value })}>
            <option>High</option><option>Medium</option><option>Low</option>
          </select></div>
      </div>
      <div className="fr">
        <div className="fg"><label>Assigned Date</label><input type="date" className="fc" value={f.assigned_date} onChange={e => setF({ ...f, assigned_date: e.target.value })} /></div>
        <div className="fg"><label>Due Date *</label><input data-testid="task-due" type="date" className="fc" value={f.due_date} onChange={e => setF({ ...f, due_date: e.target.value })} /></div>
      </div>
      <div className="fg"><label>Status</label>
        <select className="fc" value={f.status} onChange={e => setF({ ...f, status: e.target.value })}>
          <option>Pending</option><option>In Process</option><option>Completed</option></select></div>
      <div className="fg"><label>Description</label><textarea className="fc" value={f.description} onChange={e => setF({ ...f, description: e.target.value })} /></div>
      <div className="fg"><label>Activities / Subtasks</label>
        <div className="aclist">
          {f.activities.map((a, i) => (
            <div key={i} className="acitem">
              <input type="checkbox" className="acb" checked={!!a.done} onChange={e => { const na = [...f.activities]; na[i].done = e.target.checked; setF({ ...f, activities: na }); }} />
              <input className="act" value={a.text} onChange={e => { const na = [...f.activities]; na[i].text = e.target.value; setF({ ...f, activities: na }); }} placeholder="Activity name" />
              <button className="acd" onClick={() => setF({ ...f, activities: f.activities.filter((_, j) => j !== i) })}>×</button>
            </div>
          ))}
        </div>
        <button className="aab" onClick={() => setF({ ...f, activities: [...f.activities, { text: "", done: false }] })}>+ Add Activity</button>
      </div>
      {(
        <div className="fg"><label>Attachments (max 10MB each)</label>
          <p>Choose files now. They will upload when you click Save Task.</p>
          <input aria-label="Add attachments" type="file" multiple onChange={uploadFile} disabled={uploading} style={{ marginTop: 6, fontSize: 16, maxWidth: "100%" }} />
          {pendingFiles.map((file, index) => <div className="att-item" key={index}>
            <span className="att-name">📎 {file.name} — waiting to upload</span>
            <button type="button" className="btn bs bsm" disabled={uploading} onClick={() => setPendingFiles(previous => previous.filter((_, i) => i !== index))}>Remove</button>
          </div>)}
          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
            {attachments.map(a => (
              <div key={a.id} className="att-item">
                <span className="att-name">📎 {a.name}</span>
                <span style={{ color: "var(--mt)", fontSize: 11 }}>{Math.round(a.size / 1024)} KB</span>
                <a className="btn bs bsm" href="#download" onClick={async (e) => {
                  e.preventDefault();
                  const blob = await downloadAttachment(a); const url = URL.createObjectURL(blob);
                  const link = document.createElement("a"); link.href = url; link.download = a.name; link.click();
                }}>Download</a>
              </div>
            ))}
          </div>
        </div>
      )}
      <TaskComments taskId={savedTask.current?.id} />
    </Modal>
  );
}

// ---------- Task Page ----------
function Tasks({ tasks, users, groups, currentUser, reload }) {
  const [q, setQ] = useState(""); const [fs, setFs] = useState("all"); const [fu, setFu] = useState("all");
  const [editing, setEditing] = useState(null); const [showNew, setShowNew] = useState(false);
  const { push } = useToast();
  const canAssign = currentUser.role === "owner" || currentUser.can_assign;

  const filtered = useMemo(() => tasks.filter(t => {
    if (fs !== "all" && t.status !== fs) return false;
    if (fu !== "all" && t.assigned_to !== fu) return false;
    if (q && !t.name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [tasks, q, fs, fu]);

  const staffName = (id) => users.find(u => u.id === id)?.name || "—";
  const del = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    try { await api.del(`/tasks/${id}`); push("Task deleted", "ok"); reload(); }
    catch (e) { push(errMsg(e), "err"); }
  };
  const setStatus = async (t, status) => {
    try { await api.patch(`/tasks/${t.id}/status`, { status }); reload(); }
    catch (e) { push(errMsg(e), "err"); }
  };

  return (
    <div>
      <div className="ph"><div className="pt">Task Management</div>
        {canAssign && <button data-testid="btn-add-task" className="btn bp" onClick={() => setShowNew(true)}>+ Add Task</button>}
      </div>
      <div className="fbar">
        <div className="sw"><input data-testid="task-search" className="sinp" placeholder="Search tasks..." value={q} onChange={e => setQ(e.target.value)} /></div>
        <select data-testid="task-filter-status" className="fsel" value={fs} onChange={e => setFs(e.target.value)}>
          <option value="all">All Status</option><option>Pending</option><option>In Process</option><option>Completed</option></select>
        {canAssign && (
          <select className="fsel" value={fu} onChange={e => setFu(e.target.value)}>
            <option value="all">All Staff</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}
      </div>
      <div className="tc"><p className="table-scroll-hint">Swipe sideways to see task details and actions →</p><div className="tw" tabIndex={0} role="region" aria-label="Tasks, scroll horizontally for all columns"><table><thead>
        <tr><th>#</th><th>Task</th><th>Priority</th><th>Assigned To</th><th>Assigned By</th><th>Due</th><th>Progress</th><th>Status</th><th>Actions</th></tr></thead>
        <tbody data-testid="tasks-table-body">
          {filtered.map((t, i) => (
            <tr key={t.id} className={isOverdue(t) ? "rov" : ""} data-testid={`task-row-${t.id}`}>
              <td>{i + 1}</td><td><strong>{t.name}</strong>{t.description && <div style={{ fontSize: 11, color: "var(--mt)", marginTop: 2 }}>{t.description.slice(0, 60)}</div>}</td>
              <td><span className={`badge ${prioBadge(t.priority)}`}>{t.priority}</span></td>
              <td>{t.group_id ? <><div>👥 {groups.find(g => g.id === t.group_id)?.name || "Group"}</div><div style={{ fontSize: 11, color: "var(--mt)", marginTop: 2 }}>Responsible: {groups.find(g => g.id === t.group_id)?.responsible_name || "Not recorded"}</div></> : staffName(t.assigned_to)}</td>
              <td>{t.assigned_by_name || "Not recorded"}<div style={{ fontSize: 11, color: "var(--mt)", marginTop: 2 }}>{t.assigned_by_role === "owner" ? "Company Owner" : t.assigned_by_role === "staff" ? "Staff" : ""}</div></td>
              <td>{t.due_date || "—"}</td>
              <td><span className="pbar"><span className="pfil" style={{ width: `${progress(t)}%` }} /></span>{progress(t)}%</td>
              <td><select className={`ss ${t.status === "Completed" ? "don" : t.status === "In Process" ? "inp" : "pnd"}`} value={t.status} onChange={e => setStatus(t, e.target.value)}>
                <option>Pending</option><option>In Process</option><option>Completed</option></select></td>
              <td>
                <button className="ib2 ie" title="Edit" onClick={() => setEditing(t)}>✎</button>
                {canAssign && <button data-testid={`btn-del-task-${t.id}`} className="ib2 id" title="Delete" onClick={() => del(t.id)}>🗑</button>}
              </td>
            </tr>
          ))}
          {!filtered.length && <tr><td colSpan="9" className="empty">No tasks found</td></tr>}
        </tbody></table></div></div>
      {showNew && <TaskModal users={users} groups={groups} currentUser={currentUser}
        onClose={() => setShowNew(false)} onSave={() => { setShowNew(false); reload(); }} />}
      {editing && <TaskModal task={editing} users={users} groups={groups} currentUser={currentUser}
        onClose={() => setEditing(null)} onSave={() => { setEditing(null); reload(); }} />}
    </div>
  );
}

// ---------- Staff Page ----------
function StaffPage({ users, currentUser, reload }) {
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); const [showNew, setShowNew] = useState(false);
  const [resetting, setResetting] = useState(null);
  const { push } = useToast();
  const canManage = currentUser.role === "owner";

  const list = users.filter(u => (canManage ? u.id !== currentUser.id : u.id === currentUser.id)
    && (!q || u.name.toLowerCase().includes(q.toLowerCase()) || u.email.toLowerCase().includes(q.toLowerCase())));

  const del = async (id) => {
    if (!window.confirm("Remove this staff member?")) return;
    try { await api.del(`/users/${id}`); push("Staff removed", "ok"); reload(); }
    catch (e) { push(errMsg(e), "err"); }
  };

  return (
    <div>
      <div className="ph"><div className="pt">{canManage ? "Staff Management" : "My Staff Profile"}</div>
        {canManage && <button data-testid="btn-add-staff" className="btn bp" onClick={() => setShowNew(true)}>+ Add Staff</button>}
      </div>
      <div className="fbar"><div className="sw"><input className="sinp" placeholder="Search staff..." value={q} onChange={e => setQ(e.target.value)} /></div></div>
      <div className="sgrid" data-testid="staff-grid">
        {list.map(u => (
          <div className="scard" key={u.id} data-testid={`staff-card-${u.id}`}>
            <div className="sct">
              <div className="sav">{initials(u.name)}</div>
              <div style={{ flex: 1 }}>
                <div className="sn2">{u.name}</div>
                <div className="se2">{u.email}</div>
                {u.department && <div className="sm2">{u.department}{u.phone ? ` · ${u.phone}` : ""}</div>}
              </div>
            </div>
            <div className="sbdg">
              {u.role === "owner" && <span className="badge bphi">Owner</span>}
            </div>
            {canManage && u.role !== "owner" && (
              <div className="sf2">
                <button className="ib2 ie" title="Edit" onClick={() => setEditing(u)}>✎</button>
                <button className="ib2 ip" title="Reset password" onClick={() => setResetting(u)}>🔑</button>
                <button className="ib2 id" title="Remove" onClick={() => del(u.id)}>🗑</button>
              </div>
            )}
          </div>
        ))}
        {!list.length && <div className="empty">No staff yet</div>}
      </div>
      {showNew && <StaffModal onClose={() => setShowNew(false)} onSave={() => { setShowNew(false); reload(); }} />}
      {editing && <StaffModal user={editing} onClose={() => setEditing(null)} onSave={() => { setEditing(null); reload(); }} />}
      {resetting && <ResetPwModal user={resetting} onClose={() => setResetting(null)} />}
    </div>
  );
}

export function StaffModal({ user, onClose, onSave }) {
  const passwordInput = React.useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  const [f, setF] = useState({
    name: user?.name || "", email: user?.email || "",
    department: user?.department || "", phone: user?.phone || "",
    can_assign: Boolean(user?.can_assign), password: "",
  });
  const [err, setErr] = useState("");
  const { push } = useToast();
  const save = async () => {
    setErr("");
    if (!f.name.trim() || !f.email.trim()) return setErr("Name and email required");
    const password = passwordInput.current?.value ?? f.password;
    if (!user?.id && password.length < 12) return setErr(`Temporary password has ${password.length} characters; at least 12 are required.`);
    try {
      const profile = { name: f.name.trim(), department: f.department.trim(), phone: f.phone.trim(), can_assign: f.can_assign };
      if (user?.id) { await api.put(`/users/${user.id}`, profile); push("Staff updated", "ok"); }
      else { await api.post("/users", { ...profile, email: f.email.trim().toLowerCase(), password }); push("Staff added — provide the temporary password securely", "ok"); }
      onSave();
    } catch (e) { setErr(errMsg(e)); }
  };
  return (
    <Modal title={user?.id ? "Edit Staff" : "Add Staff"} onClose={onClose}
      footer={<><button className="btn bs" onClick={onClose}>Cancel</button><button data-testid="staff-save" className="btn bp" onClick={save}>Save</button></>}>
      {err && <div className="aerr">{err}</div>}
      <div className="fr">
        <div className="fg"><label>Full Name *</label><input data-testid="staff-name" className="fc" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} /></div>
        <div className="fg"><label>Email *</label><input data-testid="staff-email" className="fc" type="email" disabled={!!user?.id} value={f.email} onChange={e => setF({ ...f, email: e.target.value })} /></div>
      </div>
      <div className="fg" style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--gl)", padding: "10px 14px", borderRadius: 8, marginTop: 10 }}>
        <input type="checkbox" checked={f.can_assign} onChange={e => setF({ ...f, can_assign: e.target.checked })} style={{ width: 16, height: 16 }} />
        <div><label style={{ margin: 0 }}>📋 Can assign tasks to staff</label>
          <div style={{ fontSize: 11, color: "#166534" }}>They can assign tasks only to staff. They cannot manage staff, access backups, or assign tasks to an Owner.</div></div>
      </div>
      {!user?.id && <div className="fg">
        <label htmlFor="staff-password">Temporary Password *</label>
        <input ref={passwordInput} id="staff-password" name="new-password" autoComplete="new-password" type={showPassword ? "text" : "password"} className="fc" defaultValue="" aria-describedby="staff-password-help"
          onInput={e => { const password = e.currentTarget.value; setF(prev => ({ ...prev, password })); setErr(""); }} />
        <div id="staff-password-help" aria-live="polite">{f.password.length} characters entered — minimum 12 characters (letters, numbers or symbols).</div>
        <button type="button" className="btn bs" aria-controls="staff-password" aria-pressed={showPassword} onClick={() => setShowPassword(value => !value)}>{showPassword ? "Hide password" : "Show password"}</button>
      </div>}
      <div className="fr">
        <div className="fg"><label>Department</label><input className="fc" value={f.department} onChange={e => setF({ ...f, department: e.target.value })} /></div>
        <div className="fg"><label>Phone</label><input className="fc" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} /></div>
      </div>
      {!user?.id && <div className="ib" style={{ marginTop: 12 }}><b>🔑 Login</b>Give the staff member the temporary password through a secure channel and ask them to change it immediately.</div>}
    </Modal>
  );
}

function ResetPwModal({ user, onClose }) {
  const [p, setP] = useState(""); const [c, setC] = useState(""); const [err, setErr] = useState("");
  const { push } = useToast();
  const save = async () => {
    if (p.length < 12) return setErr("Use at least 12 characters");
    if (p !== c) return setErr("Passwords do not match");
    try { await api.post(`/users/${user.id}/reset-password`, { new_password: p }); push("Password reset", "ok"); onClose(); }
    catch (e) { setErr(errMsg(e)); }
  };
  return <Modal title="Reset Staff Password" onClose={onClose}
    footer={<><button className="btn bs" onClick={onClose}>Cancel</button><button className="btn bw2" onClick={save}>Reset</button></>}>
    {err && <div className="aerr">{err}</div>}
    <div className="ib"><b>Resetting for</b>{user.name} ({user.email})</div>
    <div className="fg"><label>New Password *</label><input type="password" className="fc" value={p} onChange={e => setP(e.target.value)} /></div>
    <div className="fg"><label>Confirm *</label><input type="password" className="fc" value={c} onChange={e => setC(e.target.value)} /></div>
  </Modal>;
}

// ---------- Groups ----------
function GroupsPage({ users, groups, currentUser, reload }) {
  const [showNew, setShowNew] = useState(false); const [editing, setEditing] = useState(null);
  const { push } = useToast();
  const canManage = currentUser.role === "owner";
  const del = async (id) => {
    if (!window.confirm("Delete this group?")) return;
    try { await api.del(`/groups/${id}`); push("Group deleted", "ok"); reload(); }
    catch (e) { push(errMsg(e), "err"); }
  };
  return (
    <div>
      <div className="ph"><div className="pt">Groups</div>
        {canManage && <button className="btn bp" onClick={() => setShowNew(true)}>+ Add Group</button>}
      </div>
      <div className="ib gr"><b>👥 About Groups</b>Assign a group of staff — all members see the task; the Responsible person is the point of contact.</div>
      <div className="sgrid">
        {groups.map(g => (
          <div className="scard" key={g.id}>
            <div className="sct">
              <div className="sav" style={{ background: "linear-gradient(135deg,var(--w),#f59e0b)" }}>{initials(g.name)}</div>
              <div style={{ flex: 1 }}>
                <div className="sn2">{g.name}</div>
                <div className="se2">{g.members.length} members</div>
                <div className="sm2">Responsible: {g.responsible_name || users.find(u => u.id === g.responsible)?.name || "—"}</div>
              </div>
            </div>
            {canManage && (
              <div className="sf2">
                <button className="ib2 ie" onClick={() => setEditing(g)}>✎</button>
                <button className="ib2 id" onClick={() => del(g.id)}>🗑</button>
              </div>
            )}
          </div>
        ))}
        {!groups.length && <div className="empty">No groups yet</div>}
      </div>
      {showNew && <GroupModal users={users} onClose={() => setShowNew(false)} onSave={() => { setShowNew(false); reload(); }} />}
      {editing && <GroupModal group={editing} users={users} onClose={() => setEditing(null)} onSave={() => { setEditing(null); reload(); }} />}
    </div>
  );
}

function GroupModal({ group, users, onClose, onSave }) {
  const [name, setName] = useState(group?.name || "");
  const [members, setMembers] = useState(group?.members || []);
  const [resp, setResp] = useState(group?.responsible || "");
  const [err, setErr] = useState("");
  const { push } = useToast();
  const toggle = (id) => setMembers(m => m.includes(id) ? m.filter(x => x !== id) : [...m, id]);
  const save = async () => {
    if (!name.trim()) return setErr("Group name required");
    if (members.length < 2) return setErr("Select at least 2 members");
    if (!members.includes(resp)) return setErr("Responsible must be one of the members");
    try {
      const data = { name, members, responsible: resp };
      if (group?.id) await api.put(`/groups/${group.id}`, data); else await api.post("/groups", data);
      push("Group saved", "ok"); onSave();
    } catch (e) { setErr(errMsg(e)); }
  };
  const usable = users.filter(u => u.role === "staff" && !u.is_system_account);
  return <Modal title={group?.id ? "Edit Group" : "Add Group"} onClose={onClose}
    footer={<><button className="btn bs" onClick={onClose}>Cancel</button><button className="btn bp" onClick={save}>Save Group</button></>}>
    {err && <div className="aerr">{err}</div>}
    <div className="fg"><label>Group Name *</label><input className="fc" value={name} onChange={e => setName(e.target.value)} /></div>
    <div className="fg"><label>Members *</label>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflowY: "auto", border: "1.5px solid var(--bd)", borderRadius: 8, padding: 10 }}>
        {usable.map(u => (
          <label key={u.id} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={members.includes(u.id)} onChange={() => toggle(u.id)} />
            <span>{u.name} <span style={{ color: "var(--mt)", fontSize: 12 }}>({u.email})</span></span>
          </label>
        ))}
      </div>
    </div>
    <div className="fg"><label>Responsible Person *</label>
      <select className="fc" value={resp} onChange={e => setResp(e.target.value)}>
        <option value="">— Select —</option>
        {members.map(id => { const u = users.find(x => x.id === id); return u && <option key={id} value={id}>{u.name}</option>; })}
      </select></div>
  </Modal>;
}

// ---------- Data & Backup ----------
function DataPage({ currentUser }) {
  const { push } = useToast();
  const downloadBackup = async () => {
    try {
      const data = await api.get("/backup/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `erawat-wam-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click(); URL.revokeObjectURL(url); push("Backup downloaded", "ok");
    } catch (e) { push(errMsg(e), "err"); }
  };
  return (
    <div>
      <div className="ph"><div className="pt">Data &amp; Storage</div></div>
      <div className="xc"><div className="xch"><div className="xct">☁️ Cloud Sync</div></div>
        <div className="xcb">
          <div className="ib ok"><b>✅ Live cloud storage enabled</b>All tasks &amp; staff are stored securely. Every office sees the same data in real time.</div>
          <div className="ib"><b>🔒 Security</b>Passwords are bcrypt-hashed. Sessions use JWT tokens. HTTPS is enforced in production.</div>
        </div>
      </div>
      <div className="xc"><div className="xch"><div className="xct">💾 Data Backup</div></div>
        <div className="xcb">
          {currentUser.role === "owner" ? <>
            <div className="ib gr"><b>🔐 Owner-only backup access</b>Backup downloads are controlled by your company Owner account.</div>
            <button className="btn bp" onClick={downloadBackup} style={{ marginBottom: 16 }}>↓ Download Data Backup</button>
            <div className="ib"><b>Off-site copy</b>Move the downloaded backup into your company OneDrive or Google Drive folder. It includes staff, groups, tasks, and attachment records; files stored as attachments need a separate off-site copy. Restores are intentionally handled by an authorised administrator, not directly in the browser.</div>
          </> : <div className="ib"><b>Backups are controlled by the company Owner.</b></div>}
        </div>
      </div>
    </div>
  );
}

// ---------- Change Password Modal ----------
function ChangePwModal({ user, onClose }) {
  const [cur, setCur] = useState(""); const [nw, setNw] = useState(""); const [cf, setCf] = useState(""); const [err, setErr] = useState("");
  const { push } = useToast();
  const save = async () => {
    if (nw.length < 12) return setErr("Use at least 12 characters");
    if (nw !== cf) return setErr("Passwords do not match");
    try { await api.post("/auth/change-password", { current: cur, new_password: nw }); push("Password changed", "ok"); onClose(); }
    catch (e) { setErr(errMsg(e)); }
  };
  return <Modal title="🔒 Change Password" onClose={onClose}
    footer={<><button className="btn bs" onClick={onClose}>Cancel</button><button className="btn bp" onClick={save}>Update</button></>}>
    {err && <div className="aerr">{err}</div>}
    <div className="ib"><b>Account</b>{user.email}</div>
    <div className="fg"><label>Current Password *</label><input type="password" className="fc" value={cur} onChange={e => setCur(e.target.value)} /></div>
    <div className="fg"><label>New Password *</label><input type="password" className="fc" value={nw} onChange={e => setNw(e.target.value)} /></div>
    <div className="fg"><label>Confirm *</label><input type="password" className="fc" value={cf} onChange={e => setCf(e.target.value)} /></div>
  </Modal>;
}

// ---------- Main App ----------
function AppShell({ user, setUser }) {
  const [page, setPage] = useState("dashboard");
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [groups, setGroups] = useState([]);
  const [showChangePw, setShowChangePw] = useState(false);
  const { push } = useToast();

  const logout = useCallback(async () => {
    await signOut();
    localStorage.removeItem("wam_token");
    localStorage.removeItem("wam_user");
    setUser(null);
  }, [setUser]);

  const reload = useCallback(async () => {
    try {
      const [u, t, g] = await Promise.all([api.get("/users"), api.get("/tasks"), api.get("/groups")]);
      setUsers(u); setTasks(t); setGroups(g);
    } catch (e) {
      if (e?.response?.status === 401) logout();
    }
  }, [logout]);

  useEffect(() => { reload(); const i = setInterval(reload, 15000); return () => clearInterval(i); }, [reload]);

  const isOwner = user.role === "owner";
  const pages = [
    { id: "dashboard", label: "Dashboard", icon: "🏠" },
    { id: "tasks", label: "Tasks", icon: "📋", count: tasks.length },
    ...(isOwner ? [
      { id: "staff", label: "Staff", icon: "👥", count: users.filter(u => u.id !== user.id).length },
      { id: "groups", label: "Groups", icon: "👨‍👩‍👧", count: groups.length },
      { id: "data", label: "Data & Backup", icon: "💾" },
      { id: "reports", label: "Reports", icon: "📊" },
    ] : []),
  ];

  return (
    <div className="app-wrap">
      <div className="nav">
        <div className="nbrand">
          <div className="nb-icon">EP</div>
          <div><span>Erawat Pharma Limited</span><small>Work Assignment &amp; Monitoring</small></div>
        </div>
        <button className="nbtn" onClick={() => setShowChangePw(true)}>🔒 Change Password</button>
        <button data-testid="btn-logout" className="nbtn" onClick={logout}>↩ Logout</button>
        <div className="nav-user">
          <div className="nav-av">{initials(user.name || user.email)}</div>
          <div><div className="nav-un">{user.name || "User"}</div><div className="nav-ur">{user.role}{user.can_assign && user.role === "staff" ? " · assigner" : ""}</div></div>
        </div>
      </div>
      <div className="body">
        <aside className="side">
          <div className="ssec">
            <div className="slbl">Menu</div>
            {pages.map(p => (
              <button type="button" key={p.id} aria-current={page === p.id ? "page" : undefined} data-testid={`nav-${p.id}`} className={`si ${page === p.id ? "on" : ""}`} onClick={() => setPage(p.id)}>
                <span>{p.icon}</span><span>{p.label}</span>
                {p.count !== undefined && <span className="scnt">{p.count}</span>}
              </button>
            ))}
          </div>
        </aside>
        <main className="main" data-testid="main-content">
          {page === "dashboard" && <Dashboard tasks={tasks} users={users} />}
          {page === "tasks" && <Tasks tasks={tasks} users={users} groups={groups} currentUser={user} reload={reload} />}
          {page === "staff" && <StaffPage users={users} currentUser={user} reload={reload} />}
          {page === "groups" && <GroupsPage users={users} groups={groups} currentUser={user} reload={reload} />}
          {page === "data" && <DataPage currentUser={user} />}
          {page === "reports" && isOwner && <Reports currentUser={user} />}
        </main>
      </div>
      {showChangePw && <ChangePwModal user={user} onClose={() => setShowChangePw(false)} />}
    </div>
  );
}

function AppInner() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  useEffect(() => {
    api.get("/auth/me").then(u => setUser(u)).catch(() => localStorage.removeItem("wam_token")).finally(() => setChecking(false));
  }, []);
  if (checking) return <div className="auth-wrap"><div style={{ color: "var(--mt)" }}>Loading...</div></div>;
  return user ? <AppShell user={user} setUser={setUser} /> : <Login onLogin={setUser} />;
}

export default function App() {
  return <ToastProvider><AppInner /></ToastProvider>;
}
